import { useAtom } from "jotai";
import some from "lodash/some";
import { useRef } from "react";
import { v4 as uuid } from "uuid";

import { useHandleSetFilterParams } from "#modules/search/infra/useHandleSetFilterParams";
import { filtersParamsAtom } from "#shared/view/components/AppShell/Atoms";
import { FilterQuery } from "shared";

type Expression = FilterQuery["filterGroup"]["expressions"][number];

export const useSearchByTerm = () => {
  const [filtersParams, setFiltersParams] = useAtom(filtersParamsAtom);

  const searchTermRef = useRef<HTMLInputElement>(null);

  const { handleSetFilterParams } = useHandleSetFilterParams({
    setFiltersParams,
  });

  const shouldAddSearchFilter = (value: string | undefined): value is string => {
    const hasSameNameFilter = some(
      filtersParams.filterGroup.expressions,
      (expression) => expression.property === "name" && expression.value === value,
    );

    const hasSearchTerm = !!value;

    return hasSearchTerm && !hasSameNameFilter;
  };

  const resetSearchInput = () => {
    if (searchTermRef.current) {
      searchTermRef.current.value = "";
    }
  };

  const addSearchFilter = (searchFilter: Expression) => {
    const updatedFilters: FilterQuery = {
      ...filtersParams,
      filterGroup: {
        ...filtersParams.filterGroup,
        expressions: [...filtersParams.filterGroup.expressions, searchFilter],
      },
    };

    handleSetFilterParams(updatedFilters);
  };

  const search = (value?: string) => {
    if (shouldAddSearchFilter(value)) {
      const searchFilterExpression: Expression = {
        property: "name",
        operator: "CONTAINS",
        value: value,
        id: uuid(),
      };

      addSearchFilter(searchFilterExpression);
    }

    resetSearchInput();
  };

  return { search, searchTermRef };
};
